import React, { useState, useEffect, useCallback } from 'react';
import { perksAPI, getUploadUrl } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { GlassCard } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Search,
  MapPin,
  Tag,
  Clock,
  Star,
  Gift,
  ChevronRight,
  Utensils,
  ShoppingBag,
  Film,
  Plane,
  HeartPulse,
  Wrench,
  Car,
  GraduationCap,
  Sparkles,
  CheckCircle2,
  History,
} from 'lucide-react';

const CATEGORY_ICONS = {
  fnb: Utensils,
  shopping: ShoppingBag,
  entertainment: Film,
  travel: Plane,
  health: HeartPulse,
  services: Wrench,
  automotive: Car,
  education: GraduationCap,
  other: Gift,
};

const TYPE_LABELS = {
  discount: 'Discount',
  voucher: 'Voucher',
  freebie: 'Freebie',
  cashback: 'Cashback',
  exclusive_access: 'Exclusive',
};

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
};

export default function PerksPage() {
  const { user } = useAuth();
  const [perks, setPerks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPerk, setSelectedPerk] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [using, setUsing] = useState(false);
  const [tab, setTab] = useState('browse');
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadPerks = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (searchQuery) params.search = searchQuery;
      const res = await perksAPI.browse(params);
      setPerks(res.data.perks || []);
    } catch {
      toast.error('Failed to load perks');
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchQuery]);

  const loadCategories = async () => {
    try {
      const res = await perksAPI.getCategories();
      setCategories(res.data || []);
    } catch { /* ignore */ }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await perksAPI.getMyHistory();
      setHistory(res.data || []);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadCategories(); }, []);
  useEffect(() => { if (tab === 'browse') loadPerks(); }, [loadPerks, tab]);
  useEffect(() => { if (tab === 'history') loadHistory(); }, [tab]);

  const openDetail = async (perk) => {
    try {
      const res = await perksAPI.getDetail(perk.id);
      setSelectedPerk(res.data);
    } catch {
      setSelectedPerk(perk);
    }
    setShowDetail(true);
  };

  const handleUsePerk = async () => {
    if (!selectedPerk) return;
    setUsing(true);
    try {
      await perksAPI.usePerk(selectedPerk.id);
      toast.success('Perk activated! Show this to the merchant.');
      setShowConfirm(true);
      setShowDetail(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to use perk');
    } finally {
      setUsing(false);
    }
  };

  const CatIcon = ({ catId, className }) => {
    const Icon = CATEGORY_ICONS[catId] || Gift;
    return <Icon className={className} />;
  };

  return (
    <div className="space-y-6" data-testid="perks-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Perks & Benefits</h1>
        <p className="text-gray-500 mt-1">Exclusive deals and promotions for you</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit" data-testid="perks-tabs">
        <button
          onClick={() => setTab('browse')}
          data-testid="perks-tab-browse"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            tab === 'browse' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Gift className="w-4 h-4 inline mr-1.5 -mt-0.5" />Browse
        </button>
        <button
          onClick={() => setTab('history')}
          data-testid="perks-tab-history"
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
            tab === 'history' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <History className="w-4 h-4 inline mr-1.5 -mt-0.5" />My History
        </button>
      </div>

      {tab === 'browse' && (
        <>
          {/* Search */}
          <div className="relative" data-testid="perks-search">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search perks, merchants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="perks-search-input"
            />
          </div>

          {/* Categories */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="perks-categories">
            <button
              onClick={() => setSelectedCategory('')}
              className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                !selectedCategory
                  ? 'bg-[#4E50FF] text-white border-[#4E50FF]'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#4E50FF]/30'
              }`}
              data-testid="perks-category-all"
            >
              All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`perks-category-${cat.id}`}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
                  selectedCategory === cat.id
                    ? 'text-white border-transparent'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                }`}
                style={selectedCategory === cat.id ? { backgroundColor: cat.color } : {}}
              >
                <CatIcon catId={cat.id} className="w-4 h-4" />
                {cat.name}
              </button>
            ))}
          </div>

          {/* Perks Grid */}
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#4E50FF]" />
            </div>
          ) : perks.length === 0 ? (
            <GlassCard className="text-center py-12">
              <Gift className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No perks available</p>
              <p className="text-gray-400 text-sm mt-1">Check back soon for new deals!</p>
            </GlassCard>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="perks-grid">
              {perks.map((perk) => {
                const catInfo = categories.find((c) => c.id === perk.category);
                return (
                  <GlassCard
                    key={perk.id}
                    hover
                    className="p-0 overflow-hidden"
                    onClick={() => openDetail(perk)}
                    data-testid={`perk-card-${perk.id}`}
                  >
                    {/* Image */}
                    <div
                      className="h-36 bg-gradient-to-br from-gray-100 to-gray-50 relative"
                      style={
                        perk.image_url
                          ? { backgroundImage: `url(${getUploadUrl(perk.image_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                          : {}
                      }
                    >
                      {!perk.image_url && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <CatIcon catId={perk.category} className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      {perk.is_featured && (
                        <span className="absolute top-3 left-3 bg-amber-400 text-amber-900 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Star className="w-3 h-3" />Featured
                        </span>
                      )}
                      {perk.discount_value && (
                        <span className="absolute top-3 right-3 bg-[#4E50FF] text-white text-sm font-bold px-3 py-1 rounded-lg shadow">
                          {perk.discount_value}
                        </span>
                      )}
                    </div>
                    {/* Content */}
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-gray-900 truncate">{perk.title}</h3>
                          <p className="text-sm text-gray-500 truncate">{perk.merchant_name}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0 mt-1" />
                      </div>
                      <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                        {catInfo && (
                          <span className="flex items-center gap-1" style={{ color: catInfo.color }}>
                            <CatIcon catId={perk.category} className="w-3.5 h-3.5" />
                            {catInfo.name}
                          </span>
                        )}
                        {perk.perk_type && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3.5 h-3.5" />{TYPE_LABELS[perk.perk_type] || perk.perk_type}
                          </span>
                        )}
                      </div>
                      {perk.location?.city && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
                          <MapPin className="w-3.5 h-3.5" />
                          {perk.location.city}{perk.location.state ? `, ${perk.location.state}` : ''}
                        </div>
                      )}
                    </div>
                  </GlassCard>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div data-testid="perks-history">
          {historyLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#4E50FF]" />
            </div>
          ) : history.length === 0 ? (
            <GlassCard className="text-center py-12">
              <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No usage history yet</p>
              <p className="text-gray-400 text-sm mt-1">Start using perks to see them here</p>
            </GlassCard>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <GlassCard key={item.id} className="flex items-center gap-4 p-4">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{item.perk?.title || 'Unknown Perk'}</p>
                    <p className="text-xs text-gray-500">{item.perk?.merchant_name}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">{formatDate(item.used_at)}</p>
                    {item.perk?.discount_value && (
                      <p className="text-sm font-semibold text-[#4E50FF]">{item.perk.discount_value}</p>
                    )}
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Perk Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-md" data-testid="perk-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg">{selectedPerk?.title}</DialogTitle>
          </DialogHeader>
          {selectedPerk && (
            <div className="space-y-4">
              {/* Image */}
              {selectedPerk.image_url && (
                <div
                  className="h-40 rounded-xl bg-cover bg-center bg-gray-100"
                  style={{ backgroundImage: `url(${getUploadUrl(selectedPerk.image_url)})` }}
                />
              )}

              {/* Discount badge */}
              {selectedPerk.discount_value && (
                <div className="flex items-center gap-2">
                  <span className="bg-[#4E50FF]/10 text-[#4E50FF] text-lg font-bold px-4 py-2 rounded-xl">
                    {selectedPerk.discount_value}
                  </span>
                  <span className="text-sm text-gray-500">{TYPE_LABELS[selectedPerk.perk_type] || selectedPerk.perk_type}</span>
                </div>
              )}

              {/* Merchant & Location */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-700">{selectedPerk.merchant_name}</span>
                </div>
                {selectedPerk.location?.address && (
                  <div className="flex items-start gap-2 text-sm text-gray-500">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{selectedPerk.location.address}{selectedPerk.location.city ? `, ${selectedPerk.location.city}` : ''}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Valid until {formatDate(selectedPerk.valid_until)}</span>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-sm text-gray-600 leading-relaxed">{selectedPerk.description}</p>
              </div>

              {/* T&C */}
              {selectedPerk.terms_conditions && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-xs font-medium text-gray-500 mb-1">Terms & Conditions</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{selectedPerk.terms_conditions}</p>
                </div>
              )}

              {/* Use Button */}
              <Button
                onClick={handleUsePerk}
                disabled={using}
                className="w-full bg-[#4E50FF] hover:bg-[#3E40EE] text-white h-12 text-base font-semibold rounded-xl"
                data-testid="use-perk-btn"
              >
                {using ? 'Processing...' : 'Use This Perk'}
              </Button>
              <p className="text-xs text-gray-400 text-center">Unlimited usage — click and show to merchant</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm text-center" data-testid="perk-confirm-dialog">
          <div className="py-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-1">Perk Activated!</h3>
            <p className="text-sm text-gray-500 mb-4">Show this screen to the merchant to enjoy your benefit.</p>
            {selectedPerk && (
              <div className="bg-[#4E50FF]/5 border border-[#4E50FF]/20 rounded-xl p-4 mb-4">
                <p className="text-lg font-bold text-[#4E50FF]">{selectedPerk.discount_value || selectedPerk.title}</p>
                <p className="text-sm text-gray-600 mt-1">{selectedPerk.merchant_name}</p>
                <p className="text-xs text-gray-400 mt-2">at {selectedPerk.location?.address || 'Store Location'}</p>
              </div>
            )}
            <Button
              onClick={() => setShowConfirm(false)}
              variant="outline"
              className="w-full"
              data-testid="perk-confirm-done-btn"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
